'use client';

import FetchUtils from '@dr-pam/common-components/Utils/FetchUtils';
import useAbortRegistry from '@dr-pam/common-components/Hooks/useAbortRegistry';
import { useMemo } from 'react';
import type { RegisterAbortFunction } from '@dr-pam/common-components/Utils/AbortUtils';

// Type definitions matching the backend response
export type ActiveUpgradePrompt = {
	prompt: {
		id: string;
		name: string;
		promptType: 'BANNER' | 'MODAL' | 'TOAST';
		frequency: 'ONCE' | 'ONCE_PER_DAY' | 'ONCE_PER_SESSION' | 'EVERY_PAGE_LOAD' | 'EVERGREEN';
		// Optional content fields - may be null
		title: string | null;
		message: string | null;
		ctaText: string | null;
		showFromDayAfterPurchase: number;
		showUntilDayAfterPurchase: number | null;
		priority: number;
		isActive: boolean;
		created: string;
		modified: string;
		upgradePathDiscount: {
			id: string;
			name: string;
			discountPercentage: number | null;
			discountAmountInCents: number | null;
		};
	};
	discountPreview: {
		discountPercentage: number | null;
		discountAmountInCents: number | null;
		eligibleSubscriptionTypeIds: string[];
	};
};

export type EngagePromptResponse = {
	engagementId: string;
};

export default class ClientUpgradeService {
	constructor(private readonly _registerAbort?: RegisterAbortFunction) {}

	/**
	 * Fetches active upgrade prompts for the current logged-in user.
	 * Returns empty array if user is not logged in or has no active trial.
	 */
	public async getActivePrompts(sessionId?: string): Promise<ActiveUpgradePrompt[]> {
		const queryParams = `?brandingType=PSP${sessionId ? `&sessionId=${encodeURIComponent(sessionId)}` : ''}`;
		const request = FetchUtils.getJson<ActiveUpgradePrompt[]>(`/api/payment/upgrade-prompts${queryParams}`);
		const abortableRequest = FetchUtils.abortableRequest(request, this._registerAbort);

		return abortableRequest.response;
	}

	/**
	 * Records when a user clicks on an upgrade prompt CTA.
	 * Returns the engagement ID which should be passed to checkout preview.
	 */
	public async engagePrompt(promptId: string, sessionId?: string): Promise<EngagePromptResponse> {
		const body: { sessionId?: string } = {};
		if (sessionId) {
			body.sessionId = sessionId;
		}

		const request = FetchUtils.postJson<EngagePromptResponse>(
			`/api/payment/upgrade-prompts/${encodeURIComponent(promptId)}/engage`,
			body,
		);
		const abortableRequest = FetchUtils.abortableRequest(request, this._registerAbort);

		return abortableRequest.response;
	}
}

export function useClientUpgradeService() {
	const registerAbort = useAbortRegistry();

	return useMemo(() => new ClientUpgradeService(registerAbort), [registerAbort]);
}
